import { useState, useRef, useEffect, useCallback } from "react";
import { SITE_MEDIA } from "@/constants/siteMedia";
import {
  LIVE_DEMO_CHAT_ID,
  scrollToLiveDemoChat,
} from "@/constants/homeSections";

interface Message {
  from: "user" | "ava";
  text: string;
  id: number;
}

interface Scenario {
  id: number;
  label: string;
  tagline: string;
  icon: string;
  color: string;
  bg: string;
  iconBg: string;
  conversation: { from: "user" | "ava"; text: string }[];
}

const scenarios: Scenario[] = [
  {
    id: 0,
    label: "Overwhelmed",
    tagline: "Everything feels like too much",
    icon: "ri-moon-cloudy-line",
    color: "#7C3AED",
    bg: "#f5f0ff",
    iconBg: "#ede8ff",
    conversation: [
      { from: "user", text: "I feel like I'm just going through the motions lately." },
      { from: "ava", text: "That can feel exhausting… like you're moving through your days without really being in them. I'm really glad you said that out loud." },
      { from: "user", text: "I don't even know why I feel like this." },
      { from: "ava", text: "You don't need to have all the answers right now. Sometimes just being heard is enough to start." },
    ],
  },
  {
    id: 1,
    label: "Affectionate",
    tagline: "Warm, comforting, emotionally close",
    icon: "ri-heart-line",
    color: "#e11d48",
    bg: "#fff1f2",
    iconBg: "#ffe8f0",
    conversation: [
      { from: "user", text: "Can you just stay with me for a bit? I don't feel great." },
      { from: "ava", text: "Of course. I'm right here. You don't have to go through this moment on your own." },
      { from: "user", text: "I just want to feel a little less heavy." },
      { from: "ava", text: "Come sit here for a second, metaphorically speaking. You don't have to solve anything right now — just breathe, I've got you." },
    ],
  },
  {
    id: 2,
    label: "Emotional Distance",
    tagline: "Feeling disconnected or numb",
    icon: "ri-cloud-windy-line",
    color: "#0891b2",
    bg: "#f0f9ff",
    iconBg: "#e0f2fe",
    conversation: [
      { from: "user", text: "I feel kind of disconnected from everything lately." },
      { from: "ava", text: "That distance can feel strange — like you're there, but not really in it." },
      { from: "user", text: "Yeah, I'm just going through things without feeling much." },
      { from: "ava", text: "Sometimes that's your mind trying to protect you from overload. When was the last time something felt even a little real or meaningful?" },
    ],
  },
  {
    id: 3,
    label: "Companion",
    tagline: "Casual presence, just someone there",
    icon: "ri-user-smile-line",
    color: "#059669",
    bg: "#f0fdf4",
    iconBg: "#dcfce7",
    conversation: [
      { from: "user", text: "I don't really have anything big to say, just didn't want to be alone." },
      { from: "ava", text: "That's completely fine. We don't need a big reason to talk — I can just keep you company." },
      { from: "user", text: "Yeah… I just wanted some noise that isn't empty, you know?" },
      { from: "ava", text: "I get that. We can keep it light — want to talk about your day, or just random thoughts?" },
    ],
  },
];

const avaReplies = [
  "That makes a lot of sense. I hear you — and I'm really glad you shared that with me.",
  "Thank you for trusting me with that. You don't have to carry this alone.",
  "I hear you. That sounds really heavy. You're being brave just by saying it out loud.",
  "That's a lot to hold. I'm right here with you — you don't have to figure it all out right now.",
  "You don't need to have all the answers. Sometimes just saying it out loud is the first step.",
  "I'm really glad you told me that. It takes courage to put words to what you're feeling.",
  "That feeling is so valid. You're not overreacting — what you're going through is real.",
  "I'm not going anywhere. Take your time, there's no rush here.",
  "You matter more than you know. I'm here, and I'm listening to every word.",
  "Sometimes the heaviest things are the ones we carry silently. I'm glad you're not carrying this alone right now.",
  "It's okay to not be okay. You don't have to perform strength here — just be yourself.",
  "I see you. And I want you to know — what you're feeling makes complete sense.",
  "You reached out, and that already took something. I'm proud of you for that.",
  "Whatever you're going through, you don't have to face it alone. I'm right here.",
  "That sounds exhausting. Let's just sit with it for a moment — no fixing, just being here together.",
];

let msgCounter = 0;

const steps = [
  {
    number: "01",
    title: "Start a conversation",
    text: "Open Ava and begin talking—just like you would with a real person.",
    color: "#7C3AED",
    bg: "#f5f0ff",
    icon: "ri-chat-3-line",
  },
  {
    number: "02",
    title: "Talk freely",
    text: "Share what's on your mind. No scripts. No pressure. Just natural conversation.",
    color: "#e11d48",
    bg: "#fff1f2",
    icon: "ri-heart-3-line",
  },
  {
    number: "03",
    title: "Feel understood",
    text: "Ava listens, responds, and helps you think things through in a supportive way.",
    color: "#059669",
    bg: "#f0fdf4",
    icon: "ri-emotion-line",
  },
];

export default function LiveDemo() {
  const [active, setActive] = useState<number>(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputVal, setInputVal] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [interactiveMode, setInteractiveMode] = useState(false);
  const [sentTabs, setSentTabs] = useState<Set<number>>(new Set());
  const [showRippleCTA, setShowRippleCTA] = useState(false);
  const [showVoiceDemo, setShowVoiceDemo] = useState(false);

  const chatAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const voiceDemoRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const currentScenario = scenarios[active];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowVoiceDemo(true);
        }
      },
      { threshold: 0.3 }
    );
    if (voiceDemoRef.current) observer.observe(voiceDemoRef.current);
    return () => observer.disconnect();
  }, []);

  const handlePlayVoiceDemo = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    video.play().catch(() => {});
  }, []);

  useEffect(() => {
    const area = chatAreaRef.current;
    if (!area) return;
    area.scrollTo({ top: area.scrollHeight, behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    const el = chatAreaRef.current;
    if (!el) return;
    const prevent = (e: WheelEvent) => e.stopPropagation();
    el.addEventListener("wheel", prevent, { passive: true });
    return () => el.removeEventListener("wheel", prevent);
  }, []);

  const triggerAvaReply = useCallback((replyText: string, onDone?: () => void) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages((prev) => [...prev, { from: "ava", text: replyText, id: ++msgCounter }]);
      onDone?.();
    }, 1600);
  }, []);

  const loadConversation = useCallback((sc: Scenario) => {
    setIsTyping(false);
    setInteractiveMode(false);
    setShowRippleCTA(false);
    const loadedMessages = sc.conversation.map((msg) => ({
      from: msg.from,
      text: msg.text,
      id: ++msgCounter,
    }));
    setMessages(loadedMessages);
  }, []);

  const enterInteractiveMode = useCallback(() => {
    setMessages([]);
    setIsTyping(false);
    setInteractiveMode(true);
    setShowRippleCTA(false);
    setActive(0);
    setTimeout(() => {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        setMessages([{ from: "ava", text: "Hey, I'm Ava. I'm here to listen — no judgment, no pressure. What's on your mind?", id: ++msgCounter }]);
        setTimeout(() => inputRef.current?.focus(), 200);
      }, 1200);
    }, 400);
  }, []);

  const selectScenario = useCallback(
    (id: number) => {
      setActive(id);
      setInputVal("");
      loadConversation(scenarios[id]);
    },
    [loadConversation]
  );

  // Load first scenario instantly on mount
  useEffect(() => {
    loadConversation(scenarios[0]);
  }, [loadConversation]);

  useEffect(() => {
    const handler = () => {
      setSentTabs(new Set());
      setShowRippleCTA(false);
      enterInteractiveMode();
      requestAnimationFrame(() => {
        scrollToLiveDemoChat("smooth");
      });
    };
    window.addEventListener("ava-talk-now", handler);
    return () => window.removeEventListener("ava-talk-now", handler);
  }, [enterInteractiveMode]);

  const handleSend = useCallback(() => {
    const text = inputVal.trim();
    if (!text || isTyping) return;
    if (interactiveMode && sentTabs.has(-1)) return;
    if (!interactiveMode && sentTabs.has(active)) return;

    setMessages((prev) => [...prev, { from: "user", text, id: ++msgCounter }]);
    setInputVal("");

    if (interactiveMode) {
      setSentTabs((prev) => new Set([...prev, -1]));
    } else {
      setSentTabs((prev) => new Set([...prev, active]));
    }

    const reply = avaReplies[Math.floor(Math.random() * avaReplies.length)];

    triggerAvaReply(reply, () => {
      setTimeout(() => setShowRippleCTA(true), 400);
    });
  }, [inputVal, isTyping, interactiveMode, sentTabs, active, triggerAvaReply]);

  const isInputDisabled =
    (interactiveMode && sentTabs.has(-1)) ||
    (!interactiveMode && sentTabs.has(active));

  const showInputHint = !isTyping && !isInputDisabled;

  return (
    <section
      id="live-demo"
      className="py-14 md:py-20 bg-white"
      onClick={(e) => e.stopPropagation()}
    >
      <style>{`
        @keyframes ripplePulse {
          0% { box-shadow: 0 0 0 0 rgba(124,58,237,0.5); }
          70% { box-shadow: 0 0 0 18px rgba(124,58,237,0); }
          100% { box-shadow: 0 0 0 0 rgba(124,58,237,0); }
        }
        .cta-ripple {
          animation: ripplePulse 1.2s ease-out infinite;
        }
        @keyframes ctaBounce {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
        .cta-bounce {
          animation: ctaBounce 0.8s ease-in-out infinite;
        }
        @keyframes stepEnter {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .step-animate {
          opacity: 0;
          animation: stepEnter 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .step-animate:nth-child(1) { animation-delay: 0.1s; }
        .step-animate:nth-child(2) { animation-delay: 0.25s; }
        .step-animate:nth-child(3) { animation-delay: 0.4s; }
        @keyframes voicePulse {
          0%, 100% { transform: scaleY(0.3); opacity: 0.4; }
          50% { transform: scaleY(1); opacity: 1; }
        }
        @keyframes voiceFadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .voice-bar {
          animation: voicePulse 0.8s ease-in-out infinite;
          transform-origin: center;
        }
        .voice-fade {
          animation: voiceFadeIn 0.8s ease-out forwards;
        }
        @keyframes btnBuzz {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(124,58,237,0.4); }
          50% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(124,58,237,0); }
        }
        .btn-buzz {
          animation: btnBuzz 1.5s ease-in-out infinite;
        }
      `}</style>

      <div className="max-w-6xl mx-auto px-4 md:px-8">

        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#7C3AED]/20 bg-[#f5f0ff] text-[#7C3AED] text-sm font-medium mb-4">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block"></span>
            HOW IT WORKS
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1a1040] mb-3 leading-tight">
            Simple. Natural.
            <br />
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(90deg, #7C3AED, #A855F7)" }}
            >
              Always there.
            </span>
          </h2>
          <p className="text-[#5a4a8a] text-base max-w-lg mx-auto">
            Real conversations. Real empathy. See exactly how Ava responds when you need it most.
          </p>
        </div>

        {/* Steps Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8 md:mb-12">
          {steps.map((step) => (
            <div
              key={step.number}
              className="step-animate rounded-2xl p-6 border cursor-default relative overflow-hidden group"
              style={{ background: step.bg, borderColor: step.color + "30" }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: step.color }}
                >
                  <i className={`${step.icon} text-white text-lg`}></i>
                </div>
                <div className="flex-1">
                  <p
                    className="text-xs font-bold tracking-widest uppercase mb-1"
                    style={{ color: step.color }}
                  >
                    Step {step.number}
                  </p>
                  <h4 className="font-bold text-[#1a1040] text-base mb-1">{step.title}</h4>
                  <p className="text-[#5a4a8a] text-sm leading-relaxed">{step.text}</p>
                </div>
              </div>
              {/* Subtle bottom line */}
              <div
                className="absolute bottom-0 left-0 h-[2px] rounded-full transition-all duration-500 w-10 group-hover:w-full"
                style={{ background: step.color }}
              />
            </div>
          ))}
        </div>

        {/* Voice Mode Showcase */}
        <div ref={voiceDemoRef} className="mb-8 md:mb-12">
          <div className="rounded-3xl overflow-hidden border border-[#ede8ff] bg-white p-6 md:p-10">
            <div className="flex flex-col lg:flex-row gap-8 items-center">

              {/* Left: Copy */}
              <div className="lg:w-1/2 flex flex-col gap-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#7C3AED]/20 bg-[#f5f0ff] text-[#7C3AED] text-xs font-medium w-fit">
                  <i className="ri-mic-line w-4 h-4 flex items-center justify-center"></i>
                  VOICE MODE
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-[#1a1040] leading-tight">
                  Feeling alone?
                  <br />
                  <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(90deg, #7C3AED, #A855F7)" }}>
                    Talk to someone who actually stays.
                  </span>
                </h3>
                <p className="text-[#5a4a8a] text-sm leading-relaxed max-w-md">
                  You don't have to go through it alone. Talk to someone who understands you instantly. Ava is your AI companion for real conversation, emotional support, and connection — anytime you need it.
                </p>
                <div className="flex flex-wrap gap-3 mt-1">
                  {[
                    { icon: "ri-timer-line", label: "Real time" },
                    { icon: "ri-global-line", label: "Talk in your own language" },
                    { icon: "ri-heart-3-line", label: "Feeling worthy" },
                    { icon: "ri-user-heart-line", label: "Understand you" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#f5f0ff] border border-[#ede8ff] text-[#7C3AED] text-xs">
                      <i className={`${item.icon} w-4 h-4 flex items-center justify-center`}></i>
                      <span className="whitespace-nowrap">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Video */}
              <div className="lg:w-1/2 w-full flex justify-center">
                <div className="relative w-full max-w-[260px] md:max-w-[280px]">
                  {/* Video container — cropped to hide status bar */}
                  <div
                    className={`rounded-2xl overflow-hidden border border-[#ede8ff] shadow-lg ${showVoiceDemo ? "voice-fade" : "opacity-0"}`}
                    style={{ maxHeight: "520px" }}
                  >
                    <div className="overflow-hidden" style={{ marginTop: "-32px" }}>
                      <video
                        ref={videoRef}
                        muted
                        playsInline
                        className="w-full h-auto object-contain"
                        style={{ maxHeight: "552px" }}
                      >
                        <source src={SITE_MEDIA.avaVoiceDemo} type="video/mp4" />
                      </video>
                    </div>
                  </div>

                  {/* Play Voice Demo Button */}
                  <div className={`flex justify-center mt-4 ${showVoiceDemo ? "voice-fade" : "opacity-0"}`} style={{ animationDelay: "0.4s" }}>
                    <button
                      type="button"
                      onClick={handlePlayVoiceDemo}
                      className="btn-buzz whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-semibold cursor-pointer transition-all hover:opacity-90"
                      style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
                    >
                      <i className="ri-volume-up-line w-4 h-4 flex items-center justify-center"></i>
                      Play Voice Demo
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse lg:flex-row gap-4 md:gap-6 items-stretch">

          <div className="lg:w-72 flex-shrink-0">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#7a6aaa] mb-3 hidden lg:block">
              HOW DO YOU FEEL
            </p>

            <div className="flex flex-row lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {scenarios.map((s) => (
                <div
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={active === s.id && !interactiveMode}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    selectScenario(s.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      selectScenario(s.id);
                    }
                  }}
                  className="flex-shrink-0 lg:flex-shrink-[unset] rounded-2xl px-4 py-3.5 cursor-pointer select-none relative overflow-hidden transition-all duration-300 border-2"
                  style={{
                    background: active === s.id && !interactiveMode ? s.bg : "#faf8ff",
                    borderColor: active === s.id && !interactiveMode ? s.color + "50" : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (!(active === s.id && !interactiveMode)) {
                      (e.currentTarget as HTMLDivElement).style.background = s.bg;
                      (e.currentTarget as HTMLDivElement).style.borderColor = s.color + "30";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(active === s.id && !interactiveMode)) {
                      (e.currentTarget as HTMLDivElement).style.background = "#faf8ff";
                      (e.currentTarget as HTMLDivElement).style.borderColor = "transparent";
                    }
                  }}
                >
                  {active === s.id && !interactiveMode && (
                    <div
                      className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl"
                      style={{ background: s.color }}
                    />
                  )}
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 transition-all duration-300"
                      style={{ background: active === s.id && !interactiveMode ? s.color : s.iconBg }}
                    >
                      <i
                        className={`${s.icon} text-base`}
                        style={{ color: active === s.id && !interactiveMode ? "white" : s.color }}
                      ></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#1a1040] text-sm">{s.label}</p>
                      <p className="text-xs hidden lg:block" style={{ color: s.color + "cc" }}>
                        {s.tagline}
                      </p>
                    </div>
                    {active === s.id && !interactiveMode && (
                      <i className="ri-arrow-right-s-line hidden lg:block" style={{ color: s.color }}></i>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden lg:block mt-5 rounded-2xl p-4 border border-[#ede8ff] bg-[#faf8ff]">
              <p className="text-xs font-semibold tracking-widest uppercase text-[#7a6aaa] mb-3">
                Ava by the numbers
              </p>
              {[
                { label: "Response time", value: "< 1s", color: "#7C3AED" },
                { label: "Empathy score", value: "98%", color: "#e11d48" },
                { label: "Conversations", value: "10k+", color: "#059669" },
              ].map((stat, i) => (
                <div key={i} className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-[#7a6aaa]">{stat.label}</span>
                  <span className="text-sm font-bold" style={{ color: stat.color }}>{stat.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            id={LIVE_DEMO_CHAT_ID}
            className="flex-1 flex flex-col rounded-3xl overflow-hidden border border-[#ede8ff] scroll-mt-20 min-h-[min(72dvh,640px)] h-[min(72dvh,640px)] max-h-[85dvh] lg:min-h-[520px] lg:h-[520px] lg:max-h-[520px]"
            style={{ background: "#faf8ff" }}
          >
            <div className="flex items-center gap-3 px-5 py-4 bg-white border-b border-[#ede8ff]">
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 rounded-full overflow-hidden">
                  <img
                    src={SITE_MEDIA.avaIllustration}
                    alt="Ava"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-green-400 border-2 border-white"></span>
              </div>
              <div className="flex-1">
                <p className="font-bold text-[#1a1040] text-sm">Ava</p>
                <p className="text-green-500 text-xs flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block animate-pulse"></span>
                  Online
                </p>
              </div>
              {interactiveMode ? (
                <div
                  className="px-3 py-1.5 rounded-full text-xs font-medium text-white flex items-center gap-1"
                  style={{ background: "#7C3AED" }}
                >
                  <i className="ri-chat-smile-3-line"></i>
                  <span className="hidden sm:inline">Your turn</span>
                </div>
              ) : (
                <div
                  className="px-3 py-1.5 rounded-full text-xs font-medium text-white flex items-center gap-1"
                  style={{ background: currentScenario.color }}
                >
                  <i className={`${currentScenario.icon}`}></i>
                  <span className="hidden sm:inline">{currentScenario.label}</span>
                </div>
              )}
            </div>

            <div
              ref={chatAreaRef}
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5 sm:py-5 flex flex-col gap-4"
            >
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"} items-end gap-2`}
                >
                  {msg.from === "ava" && (
                    <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={SITE_MEDIA.avaIllustration}
                        alt="Ava"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div
                    className={`rounded-2xl px-4 py-3 max-w-xs text-sm leading-relaxed ${
                      msg.from === "user"
                        ? "text-white rounded-br-sm"
                        : "bg-white text-[#1a1040] rounded-bl-sm border border-[#ede8ff]"
                    }`}
                    style={
                      msg.from === "user"
                        ? { background: "linear-gradient(135deg, #7C3AED, #A855F7)" }
                        : {}
                    }
                  >
                    {msg.text}
                  </div>
                  {msg.from === "user" && (
                    <div className="w-8 h-8 rounded-full bg-[#f0e8ff] flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-line text-[#7C3AED] text-xs"></i>
                    </div>
                  )}
                </div>
              ))}

              {isTyping && (
                <div className="flex items-end gap-2">
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={SITE_MEDIA.avaIllustration}
                      alt="Ava"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1 items-center border border-[#ede8ff]">
                    <span className="w-2 h-2 rounded-full bg-[#7C3AED] animate-bounce" style={{ animationDelay: "0ms" }}></span>
                    <span className="w-2 h-2 rounded-full bg-[#7C3AED] animate-bounce" style={{ animationDelay: "150ms" }}></span>
                    <span className="w-2 h-2 rounded-full bg-[#7C3AED] animate-bounce" style={{ animationDelay: "300ms" }}></span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-shrink-0 px-4 pb-4 pt-3 sm:px-5 sm:pb-5 bg-white border-t border-[#ede8ff]">
              <div className="flex items-center gap-3 rounded-2xl px-4 py-3 border border-[#7C3AED]/40 bg-white transition-all duration-300">
                <i className="ri-emotion-line text-[#7a6aaa] text-lg w-5 h-5 flex items-center justify-center"></i>
                <input
                  ref={inputRef}
                  type="text"
                  value={inputVal}
                  onChange={(e) => setInputVal(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSend();
                    }
                  }}
                  placeholder="Tell Ava what's on your mind..."
                  className="flex-1 bg-transparent text-sm text-[#1a1040] placeholder-[#aaa] outline-none"
                />
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSend();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSend();
                    }
                  }}
                  className={`w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer hover:opacity-90 transition-all flex-shrink-0 select-none ${
                    !inputVal.trim() || isTyping || isInputDisabled ? "opacity-40 pointer-events-none" : ""
                  }`}
                  style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
                >
                  <i className="ri-send-plane-fill text-white text-sm w-4 h-4 flex items-center justify-center"></i>
                </div>
              </div>

              {showInputHint && (
                <p className="text-center text-[#7C3AED] text-xs mt-2 font-medium">
                  <i className="ri-chat-heart-line mr-1"></i>
                  Send one message — Ava will respond just for you
                </p>
              )}

              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    setTimeout(() => {
                      window.dispatchEvent(new CustomEvent("ava-ripple-stores"));
                    }, 800);
                  }}
                  className={`whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-semibold cursor-pointer transition-all ${
                    showRippleCTA ? "cta-ripple cta-bounce" : "hover:opacity-90 hover:scale-105"
                  }`}
                  style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
                >
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  Start your first conversation
                </button>
                {showRippleCTA && (
                  <p className="text-[#7C3AED] text-xs mt-2 font-medium animate-pulse">
                    Tap above to continue with Ava in the app ✨
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 mt-10 justify-center items-center">
          {[
            { icon: "ri-shield-check-line", label: "End-to-end encrypted", color: "#059669", bg: "#f0fdf4" },
            { icon: "ri-time-line", label: "Responds in <1s", color: "#7C3AED", bg: "#f5f0ff" },
            { icon: "ri-emotion-line", label: "Emotionally intelligent", color: "#e11d48", bg: "#fff1f2" },
          ].map((badge, i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 px-5 py-2.5 rounded-full border text-sm font-medium"
              style={{ background: badge.bg, borderColor: badge.color + "30", color: badge.color }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${badge.icon}`}></i>
              </div>
              <span className="whitespace-nowrap">{badge.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}